import { useEffect, useMemo, useState } from 'react';
import { useUsers } from '../../../hooks/admin/useUsers';
import { usePendingAccounts, useDeletePendingAccounts, useResendInvite } from '../../../hooks/admin/usePendingAccounts';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import { useTableUrlState } from '../../../hooks/useTableUrlState';
import { useColumnVisibility } from '../../../hooks/useColumnVisibility';
import UserTable, { USER_COLUMNS, USER_COLUMNS_DEFAULT_HIDDEN } from '../../../components/admin/UserTable';
import PendingAccountsTable from '../../../components/admin/PendingAccountsTable';
import DeleteDialog from '../../../components/admin/DeleteDialog';
import Pagination from '../../../components/admin/Pagination';
import SearchInput from '../../../components/admin/SearchInput';
import ColumnVisibilityMenu from '../../../components/admin/ColumnVisibilityMenu';
import UserInviteDialog from '../../../components/admin/UserInviteDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import type { PendingAccount } from '../../../types/admin';

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'partner', label: 'Partner' },
  { value: 'customer', label: 'Customer' },
  { value: 'guest', label: 'Guest' },
];

export default function UserList() {
  const { data: users, isLoading: usersLoading, error: usersError } = useUsers();
  const { data: pendingAccounts, isLoading: pendingLoading } = usePendingAccounts();
  const deletePendingAccounts = useDeletePendingAccounts();
  const resendInvite = useResendInvite();

  const [pendingToRemove, setPendingToRemove] = useState<PendingAccount | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const { page, pageSize, search, setPage, setPageSize, setSearch, getFilter, setFilter } = useTableUrlState();
  const roleFilter = getFilter('role');

  const columnVisibility = useColumnVisibility({
    storageKey: 'admin.users.hiddenColumns',
    allColumnKeys: USER_COLUMNS.map((c) => c.key),
    defaultHidden: USER_COLUMNS_DEFAULT_HIDDEN,
  });

  const debouncedSearch = useDebouncedValue(search, 200);

  const filteredUsers = useMemo(() => {
    const list = users ?? [];
    const q = debouncedSearch.trim().toLowerCase();
    return list.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        (u.email ?? '').toLowerCase().includes(q) ||
        (u.firstName ?? '').toLowerCase().includes(q) ||
        (u.lastName ?? '').toLowerCase().includes(q) ||
        (u.accountId ?? '').toLowerCase().includes(q)
      );
    });
  }, [users, debouncedSearch, roleFilter]);

  const totalCount = filteredUsers.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  useEffect(() => {
    if (!users) return;
    if (page > totalPages) setPage(totalPages);
  }, [users, page, totalPages, setPage]);

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const handleConfirmRemovePending = async () => {
    if (!pendingToRemove) return;
    try {
      await deletePendingAccounts.mutateAsync([pendingToRemove.id]);
      setPendingToRemove(null);
    } catch (error) {
      console.error('Failed to remove pending invitation:', error);
    }
  };

  const handleResendInvite = (id: string) => {
    resendInvite.mutate(id);
  };

  if (usersLoading || pendingLoading) return <div>Loading...</div>;
  if (usersError) {
    console.error('Error loading users:', usersError);
    return <div>Error loading users. Check console for details.</div>;
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Manage Users</h1>
        <button
          type="button"
          onClick={() => setShowInviteDialog(true)}
          className="inline-flex items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <FontAwesomeIcon icon={faUserPlus} className="h-4 w-4 mr-2" />
          Invite User
        </button>
      </div>

      {/* Pending Accounts Section */}
      {pendingAccounts && pendingAccounts.length > 0 && (
        <div>
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            Pending Invitations
            <span className="text-sm font-normal bg-amber-100 text-amber-800 px-2 py-1 rounded">
              {pendingAccounts.length}
            </span>
          </h2>
          <p className="text-gray-600 text-sm mb-4">
            Users who have been invited but haven't completed registration yet.
          </p>

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <PendingAccountsTable
              accounts={pendingAccounts}
              onResendInvite={handleResendInvite}
              onRemove={setPendingToRemove}
            />
          </div>
        </div>
      )}

      {/* Active Users Section */}
      <div>
        <h2 className="text-2xl font-bold mb-4">Active Users</h2>

        <div className="mb-4 flex items-center gap-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search by name, email, or account ID..."
          />
          <select
            value={roleFilter}
            onChange={(e) => setFilter('role', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            aria-label="Filter by role"
          >
            <option value="">All roles</option>
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <ColumnVisibilityMenu
            columns={USER_COLUMNS}
            hidden={columnVisibility.hidden}
            onToggle={columnVisibility.toggle}
            onReset={columnVisibility.resetToDefaults}
          />
          <div className="text-gray-600 whitespace-nowrap">
            {totalCount} user{totalCount === 1 ? '' : 's'}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <UserTable users={pagedUsers} hiddenColumns={columnVisibility.hidden} />
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalItems={totalCount}
          />
        </div>
      </div>

      {/* Remove Pending Invitation Dialog */}
      <DeleteDialog
        open={!!pendingToRemove}
        onClose={() => setPendingToRemove(null)}
        onConfirm={handleConfirmRemovePending}
        title="Remove Pending Invitation"
        message={`Are you sure you want to remove the invitation for ${pendingToRemove?.email ?? ''}? They will no longer be able to register with their invitation link.`}
        isLoading={deletePendingAccounts.isPending}
      />

      {/* Invite User Dialog */}
      <UserInviteDialog isOpen={showInviteDialog} onClose={() => setShowInviteDialog(false)} />
    </div>
  );
}
